import type { PopperPlacementType } from '@mui/material/Popper'
import { MouseEvent as ReactMouseEvent, useEffect, useRef, useState } from 'react'

// These constants mirror the CSS formula for header popover width:
//   .IntradayTradingView_HeaderPopoverPaper { width: min(620px, calc(100vw - 32px)) }
// Keeping them in one place ensures the JS offset calculation stays in sync with the CSS.
const DUAL_POPOVER_MAX_WIDTH = 620
const DUAL_POPOVER_VIEWPORT_PADDING = 32
const DUAL_POPOVER_GAP = 12

export function useDualHeaderPopovers() {
  const [currentResultInputAnchorEl, setCurrentResultInputAnchorEl] = useState<HTMLElement | null>(null)
  const [nextOptimizationInputAnchorEl, setNextOptimizationInputAnchorEl] = useState<HTMLElement | null>(null)
  const [measuredNextOptPopoverWidth, setMeasuredNextOptPopoverWidth] = useState(0)

  // Ref to the rendered "next optimization" popover paper, used to measure its actual
  // rendered width for dual-popover offset calculation and to exclude it from click-away
  // handling on the sibling popover so clicking inside one doesn't close the other.
  const nextOptPopoverPaperRef = useRef<HTMLDivElement>(null)
  // Ref to the current result popover paper, excluded from click-away on the sibling popover.
  const currentResultPopoverPaperRef = useRef<HTMLDivElement>(null)
  // Ref wrapping only the two popover toggle buttons. Excluded from both popovers' click-away
  // handling so that clicking a toggle button does not close the other popover.
  // Uses display:contents so the wrapper is invisible to flex layout while still
  // providing a tight DOM boundary for contains() checks.
  const popoverToggleButtonsRef = useRef<HTMLDivElement>(null)

  // Measure the rendered width of the right (next-optimization) popover paper so the left-popover
  // horizontal offset is always derived from the actual layout, not hard-coded estimates.
  // Re-runs whenever the right popover opens/closes; ResizeObserver keeps it current on viewport resize.
  // biome-ignore lint/correctness/useExhaustiveDependencies: nextOptimizationInputAnchorEl is the intentional trigger — when it changes the paper mounts/unmounts and the measurement must re-run.
  useEffect(() => {
    const el = nextOptPopoverPaperRef.current
    if (!el) {
      return
    }
    setMeasuredNextOptPopoverWidth(el.getBoundingClientRect().width)
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver((entries) => {
            const entry = entries[0]
            if (entry) {
              setMeasuredNextOptPopoverWidth(entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width)
            }
          })
    observer?.observe(el)
    return () => observer?.disconnect()
  }, [nextOptimizationInputAnchorEl])

  function toggleCurrentResultInputPopover(event: ReactMouseEvent<HTMLElement>): void {
    const anchor = event.currentTarget
    setCurrentResultInputAnchorEl((current) => (current ? null : anchor))
  }

  function toggleNextOptimizationInputPopover(event: ReactMouseEvent<HTMLElement>): void {
    const anchor = event.currentTarget
    setNextOptimizationInputAnchorEl((current) => (current ? null : anchor))
  }

  function closeCurrentResultInputPopover(): void {
    setCurrentResultInputAnchorEl(null)
  }

  function closeNextOptimizationInputPopover(): void {
    setNextOptimizationInputAnchorEl(null)
  }

  const areBothHeaderPopoversOpen = currentResultInputAnchorEl !== null && nextOptimizationInputAnchorEl !== null
  const sharedDualPopoverAnchorEl = nextOptimizationInputAnchorEl
  const currentResultPopoverAnchorEl = areBothHeaderPopoversOpen
    ? sharedDualPopoverAnchorEl
    : currentResultInputAnchorEl
  const currentResultPopoverPlacement: PopperPlacementType = areBothHeaderPopoversOpen ? 'bottom-end' : 'bottom-start'
  const nextOptimizationPopoverPlacement: PopperPlacementType = areBothHeaderPopoversOpen
    ? 'bottom-end'
    : 'bottom-start'
  // Use the measured rendered width when available (keeps the offset accurate on resize).
  // Fall back to a formula that mirrors the CSS exactly: min(DUAL_POPOVER_MAX_WIDTH, 100vw - DUAL_POPOVER_VIEWPORT_PADDING).
  // No lower-bound clamp — the CSS has none, so adding one here would over-shift on narrow viewports.
  const effectivePopoverWidth =
    measuredNextOptPopoverWidth > 0
      ? measuredNextOptPopoverWidth
      : Math.min(
          DUAL_POPOVER_MAX_WIDTH,
          typeof window !== 'undefined' ? window.innerWidth - DUAL_POPOVER_VIEWPORT_PADDING : DUAL_POPOVER_MAX_WIDTH
        )
  const currentResultPopoverHorizontalOffset = areBothHeaderPopoversOpen
    ? -(effectivePopoverWidth + DUAL_POPOVER_GAP)
    : 0
  const nextOptimizationPopoverHorizontalOffset = 0

  return {
    currentResultPopoverPaperRef,
    nextOptPopoverPaperRef,
    popoverToggleButtonsRef,
    currentResultPopoverAnchorEl,
    nextOptimizationInputAnchorEl,
    currentResultPopoverPlacement,
    nextOptimizationPopoverPlacement,
    currentResultPopoverHorizontalOffset,
    nextOptimizationPopoverHorizontalOffset,
    toggleCurrentResultInputPopover,
    toggleNextOptimizationInputPopover,
    closeCurrentResultInputPopover,
    closeNextOptimizationInputPopover,
  }
}

export type DualHeaderPopovers = ReturnType<typeof useDualHeaderPopovers>
